/**
 * Pályaszerkesztő – elemkatalógus és geometria.
 *
 * Minden pályaelem-blokk a saját belépőpontjából (0,0) indul, kelet felé halad,
 * és a kimenete a rácshoz igazodik. A blokkokat cellakoordinátákon rakjuk le,
 * 90°-os forgatással; a belépő- és kimenőport így mindig pontosan illeszkedik.
 *
 * A cella 260 px: ekkora a kanyarok sugara is, ami a játék fizikájához
 * (kb. 250–320 px-es városi kanyarok) illeszkedik.
 */
import type { TrackTheme, Vec2 } from '../core/types';

export const CELL = 260;

/** 0 = kelet (+x), 1 = dél (+y), 2 = nyugat (−x), 3 = észak (−y). */
export type PortDir = 0 | 1 | 2 | 3;

export const DIR_VEC: Record<PortDir, Vec2> = {
  0: { x: 1, y: 0 },
  1: { x: 0, y: 1 },
  2: { x: -1, y: 0 },
  3: { x: 0, y: -1 },
};

export const DIR_NAME: Record<PortDir, string> = { 0: 'kelet', 1: 'dél', 2: 'nyugat', 3: 'észak' };

export type PieceType =
  | 'start'
  | 'straight1'
  | 'straight2'
  | 'straight3'
  | 'curveL'
  | 'curveR'
  | 'hairpinL'
  | 'hairpinR'
  | 'sCurveL'
  | 'sCurveR';

export type PieceGroup = 'start' | 'straight' | 'curve' | 'special';

export interface PieceTemplate {
  type: PieceType;
  /** magyar név a palettán */
  name: string;
  group: PieceGroup;
  /** rövid leírás (méret, kimenet) */
  hint: string;
  /** kimenőport cellában, a blokk saját koordinátáin */
  exit: { cx: number; cy: number; dir: PortDir };
  /** helyi vonal pixelben, (0,0)-ból kelet felé */
  path: Vec2[];
  /** ívhossz pixelben (a paletta és a statisztika használja) */
  length: number;
  /** hány cellát foglal el (tájékoztató) */
  cells: string;
}

const arc = (cx: number, cy: number, r: number, a0: number, a1: number, steps = 16): Vec2[] => {
  const out: Vec2[] = [];
  for (let i = 0; i <= steps; i++) {
    const a = a0 + ((a1 - a0) * i) / steps;
    out.push({ x: cx + Math.cos(a) * r, y: cy + Math.sin(a) * r });
  }
  return out;
};

const straight = (cells: number): Vec2[] => [
  { x: 0, y: 0 },
  { x: cells * CELL, y: 0 },
];

/** A vonal első és utolsó pontját pontosan a portokra igazítja. */
const normalize = (path: Vec2[], exit: { cx: number; cy: number }): Vec2[] => {
  const out = path.map((p) => ({ x: p.x, y: p.y }));
  out[0] = { x: 0, y: 0 };
  out[out.length - 1] = { x: exit.cx * CELL, y: exit.cy * CELL };
  return out;
};

const pathLength = (path: Vec2[]): number => {
  let sum = 0;
  for (let i = 1; i < path.length; i++) sum += Math.hypot(path[i].x - path[i - 1].x, path[i].y - path[i - 1].y);
  return sum;
};

const make = (
  type: PieceType,
  name: string,
  group: PieceGroup,
  hint: string,
  cells: string,
  exit: { cx: number; cy: number; dir: PortDir },
  path: Vec2[],
): PieceTemplate => {
  const normalized = normalize(path, exit);
  return { type, name, group, hint, cells, exit, path: normalized, length: pathLength(normalized) };
};

/* A kanyarok iránya a képernyőn értendő (az y tengely lefelé mutat):
 * balra 90° = az óramutató járásával ellentétes ív a képen. */

const CURVE_LEFT = arc(0, -CELL, CELL, Math.PI / 2, 0);
const CURVE_RIGHT = arc(0, CELL, CELL, -Math.PI / 2, 0);
const HAIRPIN_LEFT = arc(0, -CELL, CELL, Math.PI / 2, -Math.PI / 2);
const HAIRPIN_RIGHT = arc(0, CELL, CELL, -Math.PI / 2, Math.PI / 2);

/** Balra 90°, majd jobbra 90°: átlós S, a kimenet 2 cellával jobbra és 2-vel feljebb. */
const S_LEFT: Vec2[] = [...arc(0, -CELL, CELL, Math.PI / 2, 0), ...arc(2 * CELL, -CELL, CELL, Math.PI, (3 * Math.PI) / 2)];
/** Jobbra 90°, majd balra 90°: az S tükörképe, a kimenet 2 cellával jobbra és 2-vel lejjebb. */
const S_RIGHT: Vec2[] = [...arc(0, CELL, CELL, -Math.PI / 2, 0), ...arc(2 * CELL, CELL, CELL, Math.PI, Math.PI / 2)];

export const PIECES: Record<PieceType, PieceTemplate> = {
  start: make('start', 'Rajt / cél', 'start', 'Egyenes a rajtvonallal', '1 cella', { cx: 1, cy: 0, dir: 0 }, straight(1)),
  straight1: make('straight1', 'Egyenes', 'straight', '1 cella · 260 px', '1 cella', { cx: 1, cy: 0, dir: 0 }, straight(1)),
  straight2: make('straight2', 'Hosszú egyenes', 'straight', '2 cella · 520 px', '2 cella', { cx: 2, cy: 0, dir: 0 }, straight(2)),
  straight3: make('straight3', 'Célegyenes', 'straight', '3 cella · 780 px', '3 cella', { cx: 3, cy: 0, dir: 0 }, straight(3)),
  curveL: make('curveL', 'Bal 90°', 'curve', 'Sugár: 1 cella', '1×1', { cx: 1, cy: -1, dir: 3 }, CURVE_LEFT),
  curveR: make('curveR', 'Jobb 90°', 'curve', 'Sugár: 1 cella', '1×1', { cx: 1, cy: 1, dir: 1 }, CURVE_RIGHT),
  hairpinL: make('hairpinL', 'Hajtű bal', 'special', '180°-os visszaforduló', '1×2', { cx: 0, cy: -2, dir: 2 }, HAIRPIN_LEFT),
  hairpinR: make('hairpinR', 'Hajtű jobb', 'special', '180°-os visszaforduló', '1×2', { cx: 0, cy: 2, dir: 2 }, HAIRPIN_RIGHT),
  sCurveL: make('sCurveL', 'S-kanyar bal', 'special', 'Átlós S, feljebb lép', '2×2', { cx: 2, cy: -2, dir: 0 }, S_LEFT),
  sCurveR: make('sCurveR', 'S-kanyar jobb', 'special', 'Átlós S, lejjebb lép', '2×2', { cx: 2, cy: 2, dir: 0 }, S_RIGHT),
};

export const PIECE_GROUPS: { id: PieceGroup; label: string; types: PieceType[] }[] = [
  { id: 'start', label: 'RAJT / CÉL', types: ['start'] },
  { id: 'straight', label: 'EGYENESEK', types: ['straight1', 'straight2', 'straight3'] },
  { id: 'curve', label: 'KANYAROK', types: ['curveL', 'curveR'] },
  { id: 'special', label: 'HAJTŰ ÉS S-KANYAR', types: ['hairpinL', 'hairpinR', 'sCurveL', 'sCurveR'] },
];

export interface PlacedPiece {
  /** stabil azonosító (kijelöléshez, visszavonáshoz) */
  uid: string;
  type: PieceType;
  /** a belépőport cellakoordinátája */
  x: number;
  y: number;
  /** 0..3, 90°-os lépések */
  rot: number;
}

export interface EditorDoc {
  version: 1;
  id: string;
  name: string;
  theme: TrackTheme;
  pieces: PlacedPiece[];
  updatedAt: number;
}

export interface Port {
  x: number;
  y: number;
  dir: PortDir;
}

let uidCounter = 0;
export const newUid = (): string => `p${Date.now().toString(36)}${(uidCounter++).toString(36)}`;

/** Pont forgatása 90°-os lépésekkel (a képernyőn az óramutató járásával egyezően). */
export const rotateVec = (p: Vec2, rot: number): Vec2 => {
  let x = p.x;
  let y = p.y;
  for (let i = 0; i < (rot & 3); i++) {
    const t = x;
    x = -y;
    y = t;
  }
  return { x, y };
};

export const dirAfter = (dir: PortDir, rot: number): PortDir => (((dir + rot) % 4) + 4) % 4 as PortDir;

/** A blokk helyi vonala világkoordinátákon. */
export const pieceWorldPath = (piece: PlacedPiece): Vec2[] => {
  const tpl = PIECES[piece.type];
  const ox = piece.x * CELL;
  const oy = piece.y * CELL;
  return tpl.path.map((p) => {
    const r = rotateVec(p, piece.rot);
    return { x: r.x + ox, y: r.y + oy };
  });
};

export const entryPort = (piece: PlacedPiece): Port => ({ x: piece.x, y: piece.y, dir: piece.rot as PortDir });

export const exitPort = (piece: PlacedPiece): Port => {
  const tpl = PIECES[piece.type];
  const off = rotateVec({ x: tpl.exit.cx, y: tpl.exit.cy }, piece.rot);
  return { x: piece.x + off.x, y: piece.y + off.y, dir: dirAfter(tpl.exit.dir, piece.rot) };
};

export const portKey = (p: Port): string => `${p.x},${p.y},${p.dir}`;

/** A port világ-pixelközéppontja (a port a rácscsomóponton ül). */
export const portPos = (p: Port): Vec2 => ({ x: p.x * CELL, y: p.y * CELL });

/** A blokk középső pontja (kijelöléshez, felirathoz). */
export const pieceCenter = (piece: PlacedPiece): Vec2 => {
  const pts = pieceWorldPath(piece);
  const mid = pts[Math.floor(pts.length / 2)];
  return mid;
};

export const pieceBounds = (pieces: PlacedPiece[]): { minX: number; minY: number; maxX: number; maxY: number } => {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const piece of pieces) {
    for (const p of pieceWorldPath(piece)) {
      minX = Math.min(minX, p.x);
      minY = Math.min(minY, p.y);
      maxX = Math.max(maxX, p.x);
      maxY = Math.max(maxY, p.y);
    }
  }
  if (!Number.isFinite(minX)) return { minX: -CELL * 2, minY: -CELL * 2, maxX: CELL * 2, maxY: CELL * 2 };
  return { minX, minY, maxX, maxY };
};

/** Kezdő ovál: 6×5 cellás, zárt kör, azonnal vezethető. */
export const starterPieces = (): PlacedPiece[] => {
  const mk = (type: PieceType, x: number, y: number, rot: number): PlacedPiece => ({ uid: newUid(), type, x, y, rot });
  return [
    mk('start', 3, 0, 0),
    mk('straight2', 4, 0, 0),
    mk('curveR', 6, 0, 0),
    mk('straight2', 7, 1, 1),
    mk('curveR', 7, 3, 1),
    mk('straight3', 6, 4, 2),
    mk('curveR', 3, 4, 2),
    mk('straight2', 2, 3, 3),
    mk('curveR', 2, 1, 3),
  ];
};

export const THEME_KEYS: TrackTheme[] = ['city', 'lake', 'desert', 'circuit', 'suburb', 'snow'];

export const newDocId = (): string => `sajat-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;

export const createDoc = (theme: TrackTheme = 'city', name = 'Saját pálya'): EditorDoc => ({
  version: 1,
  id: newDocId(),
  name,
  theme,
  pieces: starterPieces(),
  updatedAt: Date.now(),
});
